// Styles
import '@mdi/font/css/materialdesignicons.css';
import 'vuetify/styles';

// Vuetify
import { VuetifyOptions, createVuetify } from 'vuetify';
import { createVueI18nAdapter } from 'vuetify/locale/adapters/vue-i18n';
import { useI18n } from 'vue-i18n';
import i18n from '../helpers/i18n';

import * as components from 'vuetify/components';
import { VBtn } from 'vuetify/components/VBtn';
import * as directives from 'vuetify/directives';
import colors from 'vuetify/util/colors';
import customColors from './vuetify/primitive-colors'; // Import customColors
import '../qsr-styles.scss';

const btnDefaults = {
  variant: 'flat',
};
export const vuetifyOptions: VuetifyOptions = {
  locale: {
    // @ts-ignore next line
    adapter: createVueI18nAdapter({i18n, useI18n})
  },
  components,
  directives,
  theme: {
    themes: {
      light: {
        colors: {
          primary: customColors.primary,
          secondary: customColors.secondary,
          danger: colors.red.darken1,
          error: colors.red.darken1,
          success: colors.green.darken2,
          warning: colors.orange.darken4,
          info: customColors['sky-blue-high-light'],
          'content-high': customColors['grey-darken5'],
          'content-medium': colors.grey.darken3,
          'content-low': colors.grey.darken1,
          'content-primary': customColors.primary,
          'content-danger': colors.red.darken1,
          'content-danger-high': colors.red.darken1,
          'content-danger-low': colors.red.lighten5,
          'content-success': colors.green.darken2,
          'content-success-high': colors.green.darken2,
          'content-success-low': colors.green.lighten5,
          'content-warning': colors.orange.darken3,
          'content-warning-high': colors.orange.darken3,
          'content-warning-low': colors.orange.lighten5,
          'content-info-high': customColors['sky-blue-high-light'],
          'content-info-low': customColors['sky-blue-low-light'],
          'content-onReverse-high': colors.grey.lighten5,
          'content-onReverse-medium': colors.grey.lighten3,
          'content-onReverse-low': colors.grey.lighten1,
          'content-blue-high': customColors['blue-high-light'],
          'content-blue-medium': customColors['blue-medium-light'],
          'content-pink-high': customColors['pink-high-light'],
          'content-pink-medium': customColors['pink-medium-light'],
          'content-purple-high': customColors['purple-high-light'],
          'content-purple-medium': customColors['purple-medium-light'],
          'content-deep-purple-high': customColors['deep-purple-high-light'],
          'content-deep-purple-medium': customColors['deep-purple-medium-light'],
          'content-teal-high': customColors['teal-high-light'],
          'content-teal-medium': customColors['teal-medium-light'],
          'content-cyan-high': customColors['cyan-high-light'],
          'content-cyan-medium': customColors['cyan-medium-light'],
          'content-brown-high': customColors['brown-high-light'],
          'content-brown-medium': customColors['brown-medium-light'],
          'content-olive-high': customColors['olive-high-light'],
          'content-olive-medium': customColors['olive-medium-light'],
          'content-amber-high': customColors['amber-high-light'],
          'content-amber-medium': customColors['amber-medium-light'],
          'content-deep-orange-high': customColors['deep-orange-high-light'],
          'content-deep-orange-medium': customColors['deep-orange-medium-light'],
          'content-blue-grey-high': customColors['blue-grey-high-light'],
          'content-blue-grey-medium': customColors['blue-grey-medium-light'],

          'surface-primary': customColors.primary,
          'surface-primary-high': customColors['primary-darken1'],
          'surface-primary-low': customColors['primary-lighten5'],
          'surface-danger': colors.red.darken1,
          'surface-danger-low': colors.red.lighten5,
          'surface-success': colors.green.darken2,
          'surface-success-low': colors.green.lighten5,
          'surface-info-high': customColors['sky-blue-high-light'],
          'surface-info-low': customColors['sky-blue-low-light'],
          'surface-warning': colors.orange.darken4,
          'surface-warning-low': colors.orange.lighten5,
          'surface-main': colors.shades.white,
          'surface-alt-high': colors.grey.lighten1,
          'surface-alt-medium': colors.grey.lighten3,
          'surface-alt-low': colors.grey.lighten5,
          'surface-page': colors.grey.lighten4,
          'surface-secondary': customColors.secondary,
          'surface-secondary-high': customColors['secondary-high-light'],
          'surface-secondary-medium': customColors['secondary-medium-light'],
          'surface-secondary-low': customColors['secondary-low-light'],
          'surface-blue-low': customColors['blue-low-light'],
          'surface-blue-medium': customColors['blue-medium-light'],
          'surface-blue-high': customColors['blue-high-light'],
          'surface-pink-low': customColors['pink-low-light'],
          'surface-pink-medium': customColors['pink-medium-light'],
          'surface-pink-high': customColors['pink-high-light'],
          'surface-purple-low': customColors['purple-low-light'],
          'surface-purple-medium': customColors['purple-medium-light'],
          'surface-purple-high': customColors['purple-high-light'],
          'surface-deep-purple-low': customColors['deep-purple-low-light'],
          'surface-deep-purple-medium': customColors['deep-purple-medium-light'],
          'surface-deep-purple-high': customColors['deep-purple-high-light'],
          'surface-teal-low': customColors['teal-low-light'],
          'surface-teal-medium': customColors['teal-medium-light'],
          'surface-teal-high': customColors['teal-high-light'],
          'surface-cyan-low': customColors['cyan-low-light'],
          'surface-cyan-medium': customColors['cyan-medium-light'],
          'surface-cyan-high': customColors['cyan-high-light'],
          'surface-brown-low': customColors['brown-low-light'],
          'surface-brown-medium': customColors['brown-medium-light'],
          'surface-brown-high': customColors['brown-high-light'],
          'surface-olive-low': customColors['olive-low-light'],
          'surface-olive-medium': customColors['olive-medium-light'],
          'surface-olive-high': customColors['olive-high-light'],
          'surface-amber-low': customColors['amber-low-light'],
          'surface-amber-medium': customColors['amber-medium-light'],
          'surface-amber-high': customColors['amber-high-light'],
          'surface-deep-orange-low': customColors['deep-orange-low-light'],
          'surface-deep-orange-medium': customColors['deep-orange-medium-light'],
          'surface-deep-orange-high': customColors['deep-orange-high-light'],
          'surface-blue-grey-low': customColors['blue-grey-low-light'],
          'surface-blue-grey-medium': customColors['blue-grey-medium-light'],
          'surface-blue-grey-high': customColors['blue-grey-high-light'],

          'border-high': colors.grey.darken3,
          'border-medium': '#A2A1A1',
          'border-medium-low':colors.grey.lighten1,
          'border-low': colors.grey.lighten2,
          'border-lowest': colors.grey.lighten3,
          'border-primary': customColors.primary,
          'border-danger': colors.red.darken1,
          'border-success': colors.green.darken2,
          'border-warning': colors.orange.darken3,
          'border-info': customColors['sky-blue-high-light'],
          'border-blue-high': customColors['blue-high-light'],
          'border-blue-medium': customColors['blue-medium-light'],
          'border-pink-high': customColors['pink-high-light'],
          'border-pink-medium': customColors['pink-medium-light'],
          'border-purple-high': customColors['purple-high-light'],
          'border-purple-medium': customColors['purple-medium-light'],
          'border-deep-purple-high': customColors['deep-purple-high-light'],
          'border-deep-purple-medium': customColors['deep-purple-medium-light'],
          'border-teal-high': customColors['teal-high-light'],
          'border-teal-medium': customColors['teal-medium-light'],
          'border-cyan-high': customColors['cyan-high-light'],
          'border-cyan-medium': customColors['cyan-medium-light'],
          'border-brown-high': customColors['brown-high-light'],
          'border-brown-medium': customColors['brown-medium-light'],
          'border-olive-high': customColors['olive-high-light'],
          'border-olive-medium': customColors['olive-medium-light'],
          'border-amber-high': customColors['amber-high-light'],
          'border-amber-medium': customColors['amber-medium-light'],
          'border-deep-orange-high': customColors['deep-orange-high-light'],
          'border-deep-orange-medium': customColors['deep-orange-medium-light'],
          'border-blue-grey-high': customColors['blue-grey-high-light'],
          'border-blue-grey-medium': customColors['blue-grey-medium-light'],

          // deprecated legacy colors

          accent: '#0071FF',
          category: '#5876A8',
          subcategory: '#90A7CD',
          // secondary: "#0a204f",
          // error: "#FF5252",
          // success: "#00E871",
          // warning: "#FF9200",
          // info: '#D5B800',
          panel: '#DBE3EB',
          'light-panel': '#FAFAFA',
          disabled: '#a5a5a5',
          valid: colors.green.darken2,
          grey: '#9E9E9E',
        },
      },
      dark: {
        colors: {
          primary: customColors['primary-lighten2'],
          danger: colors.red.lighten3,
          success: colors.green.lighten3,
          error: colors.red.lighten3,
          warning: colors.orange.darken4,
          info: colors.blue.lighten2,
          'content-high': colors.grey.lighten5,
          'content-medium': colors.grey.lighten3,
          'content-low': colors.grey.lighten1,
          'content-primary': customColors['primary-lighten2'],
          'content-danger': colors.red.accent1,
          'content-danger-high': colors.red.accent1,
          'content-danger-low': '#801515',
          'content-success': colors.green.accent2,
          'content-success-high': colors.green.accent2,
          'content-success-low': '#144518',
          'content-warning': colors.orange.accent2,
          'content-warning-high': colors.orange.accent2,
          'content-warning-low': '#C35B22',
          'content-info-high': colors.blue.lighten2,
          'content-info-low': '#0A3578',
          'content-onReverse-high': customColors['grey-darken5'],
          'content-onReverse-medium': colors.grey.darken3,
          'content-onReverse-low': colors.grey.darken1,
          'content-blue-high': customColors['blue-high-dark'],
          'content-blue-medium': customColors['blue-medium-dark'],
          'content-pink-high': customColors['pink-high-dark'],
          'content-pink-medium': customColors['pink-medium-dark'],
          'content-purple-high': customColors['purple-high-dark'],
          'content-purple-medium': customColors['purple-medium-dark'],
          'content-deep-purple-high': customColors['deep-purple-high-dark'],
          'content-deep-purple-medium': customColors['deep-purple-medium-dark'],
          'content-teal-high': customColors['teal-high-dark'],
          'content-teal-medium': customColors['teal-medium-dark'],
          'content-cyan-high': customColors['cyan-high-dark'],
          'content-cyan-medium': customColors['cyan-medium-dark'],
          'content-brown-high': customColors['brown-high-dark'],
          'content-brown-medium': customColors['brown-medium-dark'],
          'content-olive-high': customColors['olive-high-dark'],
          'content-olive-medium': customColors['olive-medium-dark'],
          'content-amber-high': customColors['amber-high-dark'],
          'content-amber-medium': customColors['amber-medium-dark'],
          'content-deep-orange-high': customColors['deep-orange-high-dark'],
          'content-deep-orange-medium': customColors['deep-orange-medium-dark'],
          'content-blue-grey-high': customColors['blue-grey-high-dark'],
          'content-blue-grey-medium': customColors['blue-grey-medium-dark'],

          'surface-primary': customColors['primary-lighten2'],
          'surface-primary-high': customColors['primary-lighten5'],
          'surface-primary-low': customColors['primary-darken3'],
          'surface-danger': colors.red.accent1,
          'surface-danger-high': colors.red.accent1,
          'surface-danger-low': '#801515',
          'surface-success': colors.green.accent2,
          'surface-success-high': colors.green.accent2,
          'surface-success-low': '#144518',
          'surface-warning': colors.orange.accent2,
          'surface-warning-high': colors.orange.accent2,
          'surface-warning-low': '#C35B22',
          'surface-info-high': colors.blue.lighten2,
          'surface-info-low': '#0A3578',
          'surface-main': '#333333',
          'surface-alt-high': colors.grey.darken1,
          'surface-alt-medium': colors.grey.darken2,
          'surface-alt-low': colors.grey.darken3,
          'surface-page': customColors['grey-darken5'],
          'surface-secondary': customColors['secondary-high-dark'],
          'surface-secondary-high': customColors['secondary-high-dark'],
          'surface-secondary-medium': customColors['secondary-medium-dark'],
          'surface-secondary-low': customColors['secondary-low-dark'],
          'surface-blue-low': customColors['blue-low-dark'],
          'surface-blue-medium': customColors['blue-medium-dark'],
          'surface-blue-high': customColors['blue-high-dark'],
          'surface-pink-low': customColors['pink-low-dark'],
          'surface-pink-medium': customColors['pink-medium-dark'],
          'surface-pink-high': customColors['pink-high-dark'],
          'surface-purple-low': customColors['purple-low-dark'],
          'surface-purple-medium': customColors['purple-medium-dark'],
          'surface-purple-high': customColors['purple-high-dark'],
          'surface-deep-purple-low': customColors['deep-purple-low-dark'],
          'surface-deep-purple-medium': customColors['deep-purple-medium-dark'],
          'surface-deep-purple-high': customColors['deep-purple-high-dark'],
          'surface-teal-low': customColors['teal-low-dark'],
          'surface-teal-medium': customColors['teal-medium-dark'],
          'surface-teal-high': customColors['teal-high-dark'],
          'surface-cyan-low': customColors['cyan-low-dark'],
          'surface-cyan-medium': customColors['cyan-medium-dark'],
          'surface-cyan-high': customColors['cyan-high-dark'],
          'surface-brown-low': customColors['brown-low-dark'],
          'surface-brown-medium': customColors['brown-medium-dark'],
          'surface-brown-high': customColors['brown-high-dark'],
          'surface-olive-low': customColors['olive-low-dark'],
          'surface-olive-medium': customColors['olive-medium-dark'],
          'surface-olive-high': customColors['olive-high-dark'],
          'surface-amber-low': customColors['amber-low-dark'],
          'surface-amber-medium': customColors['amber-medium-dark'],
          'surface-amber-high': customColors['amber-high-dark'],
          'surface-deep-orange-low': customColors['deep-orange-low-dark'],
          'surface-deep-orange-medium': customColors['deep-orange-medium-dark'],
          'surface-deep-orange-high': customColors['deep-orange-high-dark'],
          'surface-blue-grey-low': customColors['blue-grey-low-dark'],
          'surface-blue-grey-medium': customColors['blue-grey-medium-dark'],
          'surface-blue-grey-high': customColors['blue-grey-high-dark'],

          'border-high': colors.grey.lighten3,
          'border-medium': '#B9B8B8',
          'border-medium-low': colors.grey.darken1,
          'border-low': colors.grey.darken2,
          'border-lowest': colors.grey.darken3,
          'border-primary': customColors['primary-lighten2'],
          'border-danger': colors.red.accent1,
          'border-success': colors.green.accent2,
          'border-warning': colors.orange.accent2,
          'border-info': customColors['sky-blue-high-dark'],
          'on-primary': customColors['grey-darken5'],
          'border-blue-high': customColors['blue-high-dark'],
          'border-blue-medium': customColors['blue-medium-dark'],
          'border-pink-high': customColors['pink-high-dark'],
          'border-pink-medium': customColors['pink-medium-dark'],
          'border-purple-high': customColors['purple-high-dark'],
          'border-purple-medium': customColors['purple-medium-dark'],
          'border-deep-purple-high': customColors['deep-purple-high-dark'],
          'border-deep-purple-medium': customColors['deep-purple-medium-dark'],
          'border-teal-high': customColors['teal-high-dark'],
          'border-teal-medium': customColors['teal-medium-dark'],
          'border-cyan-high': customColors['cyan-high-dark'],
          'border-cyan-medium': customColors['cyan-medium-dark'],
          'border-brown-high': customColors['brown-high-dark'],
          'border-brown-medium': customColors['brown-medium-dark'],
          'border-olive-high': customColors['olive-high-dark'],
          'border-olive-medium': customColors['olive-medium-dark'],
          'border-amber-high': customColors['amber-high-dark'],
          'border-amber-medium': customColors['amber-medium-dark'],
          'border-deep-orange-high': customColors['deep-orange-high-dark'],
          'border-deep-orange-medium': customColors['deep-orange-medium-dark'],
          'border-blue-grey-high': customColors['blue-grey-high-dark'],
          'border-blue-grey-medium': customColors['blue-grey-medium-dark'],

          // deprecated legacy colors

          // primary: "#4F80FF",
          accent: '#3B9DFF',
          category: '#8EAAE0',
          subcategory: '#B8CBEA',
          secondary: '#3B5998',
          background: '#212121',
          // error: "#FF6B6B",
          // success: "#04944c",
          // warning: "#f07d12",
          // info: '#eeca00',
          panel: '#424242',
          'light-panel': '#212121',
          disabled: '#555555',
          valid: '#4CAF50',
          grey: '#9E9E9E',
          'grey-lighten-5': '#212121',
          'grey-lighten-4': '#424242',
          'grey-lighten-3': '#616161',
          'grey-lighten-2': '#757575',
          'grey-lighten-1': '#9E9E9E',
          'grey-darken-1': '#BDBDBD',
          'grey-darken-2': '#E0E0E0',
          'grey-darken-3': '#EEEEEE',
          'grey-darken-4': '#FAFAFA',
        },
        variables: {
          'overlay-scrim-opacity': 0.8,
        },
      },
      halloween: {
        colors: {
          primary: '#FF7518',    // Pumpkin orange
          secondary: '#6B21A8',  // Spooky purple
          danger: '#D32F2F',     // Blood red
          success: '#7FFF00',    // Slime green
          warning: '#FFB300',    // Halloween yellow-orange
          info: '#6B21A8',       // Spooky purple

          background: '#212121',
          surface: '#222222',
          'content-high': colors.grey.lighten5,
          'content-medium': colors.grey.lighten3,
          'content-low': colors.grey.lighten1,
          'content-primary': '#FF7518',
          'content-primary-high': '#FF9F47',
          'content-primary-low': '#CC5C13',
          'content-danger': '#D32F2F',
          'content-danger-high': '#E57373',
          'content-danger-low': '#801515',
          'content-success': '#7FFF00',
          'content-success-high': '#9FFF33',
          'content-success-low': '#144518',
          'content-warning': '#FFB300',
          'content-warning-high': '#FFD54F',
          'content-warning-low': '#C35B22',
          'content-info-high': '#8E44CE',
          'content-info-low': '#3E1A4D',
          'content-secondary': '#6B21A8',
          'content-secondary-high': '#8E44CE',
          'content-secondary-low': '#4A1675',
          'content-onReverse-high': customColors['grey-darken5'],
          'content-onReverse-medium': colors.grey.darken3,
          'content-onReverse-low': colors.grey.darken1,
          'content-blue-high': customColors['blue-high-dark'],
          'content-blue-medium': customColors['blue-medium-dark'],
          'content-pink-high': customColors['pink-high-dark'],
          'content-pink-medium': customColors['pink-medium-dark'],
          'content-purple-high': customColors['purple-high-dark'],
          'content-purple-medium': customColors['purple-medium-dark'],
          'content-deep-purple-high': customColors['deep-purple-high-dark'],
          'content-deep-purple-medium': customColors['deep-purple-medium-dark'],
          'content-teal-high': customColors['teal-high-dark'],
          'content-teal-medium': customColors['teal-medium-dark'],
          'content-cyan-high': customColors['cyan-high-dark'],
          'content-cyan-medium': customColors['cyan-medium-dark'],
          'content-brown-high': customColors['brown-high-dark'],
          'content-brown-medium': customColors['brown-medium-dark'],
          'content-olive-high': customColors['olive-high-dark'],
          'content-olive-medium': customColors['olive-medium-dark'],
          'content-amber-high': customColors['amber-high-dark'],
          'content-amber-medium': customColors['amber-medium-dark'],
          'content-deep-orange-high': customColors['deep-orange-high-dark'],
          'content-deep-orange-medium': customColors['deep-orange-medium-dark'],
          'content-blue-grey-high': customColors['blue-grey-high-dark'],
          'content-blue-grey-medium': customColors['blue-grey-medium-dark'],

          'surface-primary': '#FF7518',
          'surface-primary-high': '#FF9F47',
          'surface-primary-low': '#CC5C13',
          'surface-secondary': '#6B21A8',
          'surface-secondary-high': '#8E44CE',
          'surface-secondary-medium': '#7B2FB5',
          'surface-secondary-low': '#4A1675',
          'border-primary': '#FF7518',
          'border-secondary': '#6B21A8',
          'surface-danger': '#D32F2F',
          'surface-danger-high': '#E57373',
          'surface-danger-low': '#801515',
          'surface-success': '#7FFF00',
          'surface-success-high': '#9FFF33',
          'surface-success-low': '#144518',
          'surface-warning': '#FFB300',
          'surface-warning-high': '#FFD54F',
          'surface-warning-low': '#C35B22',
          'surface-info-high': '#8E44CE',
          'surface-info-low': '#3E1A4D',
          'surface-main': '#333333',
          'surface-alt-high': colors.grey.darken1,
          'surface-alt-medium': colors.grey.darken2,
          'surface-alt-low': colors.grey.darken3,
          'surface-page': customColors['grey-darken5'],
          'surface-blue-low': customColors['blue-low-dark'],
          'surface-blue-medium': customColors['blue-medium-dark'],
          'surface-blue-high': customColors['blue-high-dark'],
          'surface-pink-low': customColors['pink-low-dark'],
          'surface-pink-medium': customColors['pink-medium-dark'],
          'surface-pink-high': customColors['pink-high-dark'],
          'surface-purple-low': customColors['purple-low-dark'],
          'surface-purple-medium': customColors['purple-medium-dark'],
          'surface-purple-high': customColors['purple-high-dark'],
          'surface-deep-purple-low': customColors['deep-purple-low-dark'],
          'surface-deep-purple-medium': customColors['deep-purple-medium-dark'],
          'surface-deep-purple-high': customColors['deep-purple-high-dark'],
          'surface-teal-low': customColors['teal-low-dark'],
          'surface-teal-medium': customColors['teal-medium-dark'],
          'surface-teal-high': customColors['teal-high-dark'],
          'surface-cyan-low': customColors['cyan-low-dark'],
          'surface-cyan-medium': customColors['cyan-medium-dark'],
          'surface-cyan-high': customColors['cyan-high-dark'],
          'surface-brown-low': customColors['brown-low-dark'],
          'surface-brown-medium': customColors['brown-medium-dark'],
          'surface-brown-high': customColors['brown-high-dark'],
          'surface-olive-low': customColors['olive-low-dark'],
          'surface-olive-medium': customColors['olive-medium-dark'],
          'surface-olive-high': customColors['olive-high-dark'],
          'surface-amber-low': customColors['amber-low-dark'],
          'surface-amber-medium': customColors['amber-medium-dark'],
          'surface-amber-high': customColors['amber-high-dark'],
          'surface-deep-orange-low': customColors['deep-orange-low-dark'],
          'surface-deep-orange-medium': customColors['deep-orange-medium-dark'],
          'surface-deep-orange-high': customColors['deep-orange-high-dark'],
          'surface-blue-grey-low': customColors['blue-grey-low-dark'],
          'surface-blue-grey-medium': customColors['blue-grey-medium-dark'],
          'surface-blue-grey-high': customColors['blue-grey-high-dark'],

          'border-high': colors.grey.lighten3,
          'border-medium': '#B9B8B8',
          'border-medium-low': colors.grey.darken1,
          'border-low': colors.grey.darken2,
          'border-lowest': colors.grey.darken3,
          'border-danger': '#D32F2F',
          'border-success': '#7FFF00',
          'border-warning': '#FFB300',
          'border-info': '#6B21A8',
          'on-primary': customColors['grey-darken5'],
          'border-blue-high': customColors['blue-high-dark'],
          'border-blue-medium': customColors['blue-medium-dark'],
          'border-pink-high': customColors['pink-high-dark'],
          'border-pink-medium': customColors['pink-medium-dark'],
          'border-purple-high': customColors['purple-high-dark'],
          'border-purple-medium': customColors['purple-medium-dark'],
          'border-deep-purple-high': customColors['deep-purple-high-dark'],
          'border-deep-purple-medium': customColors['deep-purple-medium-dark'],
          'border-teal-high': customColors['teal-high-dark'],
          'border-teal-medium': customColors['teal-medium-dark'],
          'border-cyan-high': customColors['cyan-high-dark'],
          'border-cyan-medium': customColors['cyan-medium-dark'],
          'border-brown-high': customColors['brown-high-dark'],
          'border-brown-medium': customColors['brown-medium-dark'],
          'border-olive-high': customColors['olive-high-dark'],
          'border-olive-medium': customColors['olive-medium-dark'],
          'border-amber-high': customColors['amber-high-dark'],
          'border-amber-medium': customColors['amber-medium-dark'],
          'border-deep-orange-high': customColors['deep-orange-high-dark'],
          'border-deep-orange-medium': customColors['deep-orange-medium-dark'],
          'border-blue-grey-high': customColors['blue-grey-high-dark'],
          'border-blue-grey-medium': customColors['blue-grey-medium-dark'],

          // deprecated legacy colors
          accent: '#7FFF00',
          category: '#6B21A8',
          subcategory: '#A259E6',
          panel: '#222222',
          'light-panel': '#222222',
          disabled: '#555555',
          valid: '#7FFF00',
          grey: '#FFD180',
          'grey-lighten-5': '#181818',
          'grey-lighten-4': '#222222',
          'grey-lighten-3': '#3E1A4D',
          'grey-lighten-2': '#6B21A8',
          'grey-lighten-1': '#A259E6',
          'grey-darken-1': '#FFD180',
          'grey-darken-2': '#FFB300',
          'grey-darken-3': '#FF7518',
          'grey-darken-4': '#FFF8E1',

          'logo-accent-1': '#FFB300',
          'logo-accent-2': '#FF8C00',
          'logo-accent-3': '#fad378',
        },
        variables: {
          'overlay-scrim-opacity': 0.8,
        },
      },
      christmas: {
        colors: {
          primary: '#C41E3A', // Christmas red
          secondary: '#228B22', // Christmas green
          danger: '#B71C1C', // Dark red
          success: '#2E7D32', // Deep green
          warning: '#FFD700', // Gold
          info: '#1565C0', // Christmas blue

          background: '#0D1B2A',
          surface: '#1B263B',
          'content-high': colors.grey.lighten5,
          'content-medium': colors.grey.lighten3,
          'content-low': colors.grey.lighten1,
          'content-primary': '#C41E3A',
          'content-primary-high': '#E53E3E',
          'content-primary-low': '#9B1C1C',
          'content-danger': '#B71C1C',
          'content-danger-high': '#E57373',
          'content-danger-low': '#801515',
          'content-success': '#2E7D32',
          'content-success-high': '#66BB6A',
          'content-success-low': '#1B5E20',
          'content-warning': '#FFD700',
          'content-warning-high': '#FFEB3B',
          'content-warning-low': '#F57F17',
          'content-info-high': '#42A5F5',
          'content-info-low': '#0D47A1',
          'content-secondary': '#228B22',
          'content-secondary-high': '#43A047',
          'content-secondary-low': '#1B5E20',
          'content-onReverse-high': customColors['grey-darken5'],
          'content-onReverse-medium': colors.grey.darken3,
          'content-onReverse-low': colors.grey.darken1,
          'content-blue-high': customColors['blue-high-dark'],
          'content-blue-medium': customColors['blue-medium-dark'],
          'content-pink-high': customColors['pink-high-dark'],
          'content-pink-medium': customColors['pink-medium-dark'],
          'content-purple-high': customColors['purple-high-dark'],
          'content-purple-medium': customColors['purple-medium-dark'],
          'content-deep-purple-high': customColors['deep-purple-high-dark'],
          'content-deep-purple-medium': customColors['deep-purple-medium-dark'],
          'content-teal-high': customColors['teal-high-dark'],
          'content-teal-medium': customColors['teal-medium-dark'],
          'content-cyan-high': customColors['cyan-high-dark'],
          'content-cyan-medium': customColors['cyan-medium-dark'],
          'content-brown-high': customColors['brown-high-dark'],
          'content-brown-medium': customColors['brown-medium-dark'],
          'content-olive-high': customColors['olive-high-dark'],
          'content-olive-medium': customColors['olive-medium-dark'],
          'content-amber-high': customColors['amber-high-dark'],
          'content-amber-medium': customColors['amber-medium-dark'],
          'content-deep-orange-high': customColors['deep-orange-high-dark'],
          'content-deep-orange-medium': customColors['deep-orange-medium-dark'],
          'content-blue-grey-high': customColors['blue-grey-high-dark'],
          'content-blue-grey-medium': customColors['blue-grey-medium-dark'],

          'surface-primary': '#C41E3A',
          'surface-primary-high': '#E53E3E',
          'surface-primary-low': '#9B1C1C',
          'surface-secondary': '#228B22',
          'surface-secondary-high': '#43A047',
          'surface-secondary-medium': '#388E3C',
          'surface-secondary-low': '#1B5E20',
          'border-primary': '#C41E3A',
          'border-secondary': '#228B22',
          'surface-danger': '#B71C1C',
          'surface-danger-high': '#E57373',
          'surface-danger-low': '#801515',
          'surface-success': '#2E7D32',
          'surface-success-high': '#66BB6A',
          'surface-success-low': '#1B5E20',
          'surface-warning': '#FFD700',
          'surface-warning-high': '#FFEB3B',
          'surface-warning-low': '#F57F17',
          'surface-info-high': '#42A5F5',
          'surface-info-low': '#0D47A1',
          'surface-main': '#1B263B',
          'surface-alt-high': '#2D3A52',
          'surface-alt-medium': '#3F4E69',
          'surface-alt-low': '#516280',
          'surface-page': '#0D1B2A',
          'surface-blue-low': customColors['blue-low-dark'],
          'surface-blue-medium': customColors['blue-medium-dark'],
          'surface-blue-high': customColors['blue-high-dark'],
          'surface-pink-low': customColors['pink-low-dark'],
          'surface-pink-medium': customColors['pink-medium-dark'],
          'surface-pink-high': customColors['pink-high-dark'],
          'surface-purple-low': customColors['purple-low-dark'],
          'surface-purple-medium': customColors['purple-medium-dark'],
          'surface-purple-high': customColors['purple-high-dark'],
          'surface-deep-purple-low': customColors['deep-purple-low-dark'],
          'surface-deep-purple-medium': customColors['deep-purple-medium-dark'],
          'surface-deep-purple-high': customColors['deep-purple-high-dark'],
          'surface-teal-low': customColors['teal-low-dark'],
          'surface-teal-medium': customColors['teal-medium-dark'],
          'surface-teal-high': customColors['teal-high-dark'],
          'surface-cyan-low': customColors['cyan-low-dark'],
          'surface-cyan-medium': customColors['cyan-medium-dark'],
          'surface-cyan-high': customColors['cyan-high-dark'],
          'surface-brown-low': customColors['brown-low-dark'],
          'surface-brown-medium': customColors['brown-medium-dark'],
          'surface-brown-high': customColors['brown-high-dark'],
          'surface-olive-low': customColors['olive-low-dark'],
          'surface-olive-medium': customColors['olive-medium-dark'],
          'surface-olive-high': customColors['olive-high-dark'],
          'surface-amber-low': customColors['amber-low-dark'],
          'surface-amber-medium': customColors['amber-medium-dark'],
          'surface-amber-high': customColors['amber-high-dark'],
          'surface-deep-orange-low': customColors['deep-orange-low-dark'],
          'surface-deep-orange-medium': customColors['deep-orange-medium-dark'],
          'surface-deep-orange-high': customColors['deep-orange-high-dark'],
          'surface-blue-grey-low': customColors['blue-grey-low-dark'],
          'surface-blue-grey-medium': customColors['blue-grey-medium-dark'],
          'surface-blue-grey-high': customColors['blue-grey-high-dark'],

          'border-high': '#FFFFFF',
          'border-medium': '#E0E0E0',
          'border-medium-low': '#BDBDBD',
          'border-low': '#9E9E9E',
          'border-lowest': '#757575',
          'border-danger': '#B71C1C',
          'border-success': '#2E7D32',
          'border-warning': '#FFD700',
          'border-info': '#1565C0',
          'on-primary': '#FFFFFF',
          'border-blue-high': customColors['blue-high-dark'],
          'border-blue-medium': customColors['blue-medium-dark'],
          'border-pink-high': customColors['pink-high-dark'],
          'border-pink-medium': customColors['pink-medium-dark'],
          'border-purple-high': customColors['purple-high-dark'],
          'border-purple-medium': customColors['purple-medium-dark'],
          'border-deep-purple-high': customColors['deep-purple-high-dark'],
          'border-deep-purple-medium': customColors['deep-purple-medium-dark'],
          'border-teal-high': customColors['teal-high-dark'],
          'border-teal-medium': customColors['teal-medium-dark'],
          'border-cyan-high': customColors['cyan-high-dark'],
          'border-cyan-medium': customColors['cyan-medium-dark'],
          'border-brown-high': customColors['brown-high-dark'],
          'border-brown-medium': customColors['brown-medium-dark'],
          'border-olive-high': customColors['olive-high-dark'],
          'border-olive-medium': customColors['olive-medium-dark'],
          'border-amber-high': customColors['amber-high-dark'],
          'border-amber-medium': customColors['amber-medium-dark'],
          'border-deep-orange-high': customColors['deep-orange-high-dark'],
          'border-deep-orange-medium': customColors['deep-orange-medium-dark'],
          'border-blue-grey-high': customColors['blue-grey-high-dark'],
          'border-blue-grey-medium': customColors['blue-grey-medium-dark'],

          // deprecated legacy colors
          accent: '#FFD700',
          category: '#228B22',
          subcategory: '#66BB6A',
          panel: '#1B263B',
          'light-panel': '#1B263B',
          disabled: '#555555',
          valid: '#2E7D32',
          grey: '#90A4AE',
          'grey-lighten-5': '#0A1627',
          'grey-lighten-4': '#1B263B',
          'grey-lighten-3': '#2D3A52',
          'grey-lighten-2': '#3F4E69',
          'grey-lighten-1': '#516280',
          'grey-darken-1': '#FFFFFF',
          'grey-darken-2': '#F5F5F5',
          'grey-darken-3': '#E0E0E0',
          'grey-darken-4': '#FAFAFA',

          'logo-accent-1': '#DC143C', // Cranberry red
          'logo-accent-2': '#228B22', // Christmas tree green
          'logo-accent-3': '#F5F5DC', // Cream/eggnog
        },
        variables: {
          'overlay-scrim-opacity': 0.8,
        },
      },
    },
  },
  aliases: {
    VBtnPrimary: VBtn,
    VBtnSecondary: VBtn,
    VBtnTertiary: VBtn,
    VBtnPrimaryDanger: VBtn,
    VBtnSecondaryDanger: VBtn,
    VBtnTertiaryDanger: VBtn,
  },
  defaults: {
    VTooltip: {
      openOnClick: true,
    },
    VSwitch: {
      color: 'primary',
    },
    VBtn: {
      variant: 'flat',
    },
    VBtnPrimary: {
      ...btnDefaults,
      class: ['v-btn--primary'],
    },
    VBtnSecondary: {
      ...btnDefaults,
      class: ['v-btn--secondary'],
    },
    VBtnTertiary: {
      ...btnDefaults,
      variant: 'text',
      class: ['v-btn--tertiary'],
    },
    VBtnPrimaryDanger: {
      ...btnDefaults,
      class: ['v-btn--primary-danger'],
    },
    VBtnSecondaryDanger: {
      ...btnDefaults,
      class: ['v-btn--secondary-danger'],
    },
    VBtnTertiaryDanger: {
      ...btnDefaults,
      class: ['v-btn--tertiary-danger'],
    },
    VCard: {
      variant: 'elevated',
      class: ['q-card'],
    },
    VSheet: {
      class: ['q-sheet'],
    },
    VApp: {
      style: {
        background: 'rgba(var(--v-theme-surface-page))',
      },
    },
  },
};

export default createVuetify(
  // https://vuetifyjs.com/en/introduction/why-vuetify/#feature-guides
  vuetifyOptions,
);
